// WindAltitudeControl — UI affordance for the wind-particles layer.
//
// Shipped 2026-05-20 (commit 3 of the wind-particles feature).
//
// Renders ONLY when layers.wind is true. Two controls in one component:
//
//   1. Vertical altitude slider on the right edge (pinned mid-screen,
//      below the existing AltitudeSlider host). 8 discrete stops:
//      5K, 8K, 10K, 12K, 18K, 24K, 30K, 34K (FL340) ft MSL.
//
//   2. Horizontal forecast-hour scrubber along the bottom-center
//      (±24h, snaps to GFS hours 0/3/6/.../24). "NOW" label at 0,
//      "+6h", "+12h", "+24h" tick labels.
//
// Both controls write to uiStore (windAltitudeFt, windForecastHour).
// The WindParticlesController watches those values and re-fetches the
// PNG when they change.
//
// Visual style: dark glass background, white text — matches the
// rest of the map control stack. Boss locked white-on-dark.

import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useWindPrefsStore } from '../stores/windPrefsStore';

// Fire-aviation altitude ladder. Boss runs ops between ~150 AGL (tanker
// drops) and ~12,000 MSL (transit). Most useful resolution lives BELOW
// FL180. We keep two high-altitude stops (FL240, FL300) for transit cruise
// but drop FL340 — jet routes aren't a fire-aviation concern.
// 2026-05-20 14:00 PDT — lowered per boss after first mobile review.
//
// ⚠️ Data note: GFS pressure-level data starts at 850 mb (~5000 ft).
// Stops below 5000 ft currently re-use the 850 mb level until we ingest
// 1000 mb (~360 ft) and 925 mb (~2500 ft) bands. Listed as follow-on in
// data/wind-particles-plan.md.
// 0 = surface (10 m AGL wind) — shows the complex boundary-layer flow
// visible on windy.com rather than the smoother free-atmosphere pressure-level wind.
const ALT_STOPS = [0, 1500, 3000, 5000, 8000, 10000, 12000, 16000, 18000, 24000, 30000, 34000, 40000];
const HOUR_STOPS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

function fmtAlt(ft: number): string {
  if (ft === 0) return 'SFC';
  if (ft >= 18000) return `FL${Math.round(ft / 100).toString().padStart(3, '0')}`;
  if (ft >= 1000) return `${(ft / 1000).toFixed(ft % 1000 === 0 ? 0 : 1)}K`;
  return `${ft.toLocaleString()}'`;
}

function fmtHour(h: number): string {
  if (h === 0) return 'NOW';
  return `+${h}h`;
}

function snapToStop(value: number, stops: readonly number[]): number {
  let best = stops[0];
  let bestDiff = Math.abs(value - best);
  for (const s of stops) {
    const d = Math.abs(value - s);
    if (d < bestDiff) { best = s; bestDiff = d; }
  }
  return best;
}

export function WindAltitudeControl() {
  const visible = useLayerVisibilityStore((s) => s.layers.wind);
  const alt = useWindPrefsStore((s) => s.windAltitudeFt);
  const setAlt = useWindPrefsStore((s) => s.setWindAltitudeFt);
  const hour = useWindPrefsStore((s) => s.windForecastHour);
  const setHour = useWindPrefsStore((s) => s.setWindForecastHour);

  if (!visible) return null;

  // Slider value is the index into ALT_STOPS, drawn vertically so the
  // "top" of the slider is the highest altitude (FL340).
  const altIdx = ALT_STOPS.indexOf(snapToStop(alt, ALT_STOPS));
  const hourIdx = HOUR_STOPS.indexOf(snapToStop(hour, HOUR_STOPS));

  return (
    <>
      {/* Vertical altitude slider — bottom-right, just above the
          bottom controls. Z-index sits above the map but below modals. */}
      <div
        className="pointer-events-auto fixed right-3 bottom-40 z-30 flex flex-col items-center gap-2 rounded-lg bg-black/65 px-2 py-3 text-white backdrop-blur-sm shadow-lg select-none"
        style={{ width: 64 }}
      >
        <div className="text-[10px] uppercase tracking-wide opacity-70">Alt</div>
        <div className="text-sm font-mono font-bold tabular-nums">{fmtAlt(ALT_STOPS[altIdx] ?? alt)}</div>
        <input
          type="range"
          min={0}
          max={ALT_STOPS.length - 1}
          step={1}
          value={Math.max(0, altIdx)}
          onChange={(e) => setAlt(ALT_STOPS[Number(e.target.value)])}
          // Rotate -90 so the slider runs vertical, then center it.
          style={{
            writingMode: 'vertical-lr',
            direction: 'rtl',
            width: 28,
            height: 160,
          }}
          className="accent-white"
          aria-label="Wind altitude"
        />
        <div className="flex flex-col items-center text-[9px] tabular-nums opacity-60">
          <span>{fmtAlt(ALT_STOPS[ALT_STOPS.length - 1])}</span>
          <span className="my-1">·</span>
          <span>{fmtAlt(ALT_STOPS[0])}</span>
        </div>
      </div>

      {/* Horizontal forecast-hour scrubber — bottom-center, above the
          instrument bar. */}
      <div
        className="pointer-events-auto fixed left-1/2 z-30 -translate-x-1/2 rounded-lg bg-black/65 px-4 py-2 text-white backdrop-blur-sm shadow-lg select-none"
        style={{ bottom: 88, width: 320 }}
      >
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-[10px] uppercase tracking-wide opacity-70">Wind forecast</span>
          <span className="text-sm font-mono font-bold tabular-nums">{fmtHour(HOUR_STOPS[hourIdx] ?? hour)}</span>
        </div>
        <input
          type="range"
          min={0}
          max={HOUR_STOPS.length - 1}
          step={1}
          value={Math.max(0, hourIdx)}
          onChange={(e) => setHour(HOUR_STOPS[Number(e.target.value)])}
          className="w-full accent-white"
          aria-label="Wind forecast hour"
        />
        <div className="flex justify-between text-[9px] tabular-nums opacity-60 mt-0.5">
          {HOUR_STOPS.map((h, i) => (
            <span key={h} className={i === hourIdx ? 'opacity-100 font-bold' : ''}>
              {h === 0 ? 'NOW' : `+${h}`}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
